import { useState, useEffect, useMemo } from "react";
import { Row, Col, Container } from "react-bootstrap";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { FiSearch, FiX } from "react-icons/fi";
import VehicleCard from "../VehicleCard";
import FinancingCarousel from "./FinancingCarousel";
import useFinanciaciones from "../../config/useFinanciaciones";
import loading from "../../assets/loading.svg";
import { buildRouteWithUtms, saveUtmParams } from "../../config/whatsappConfig";
import { scrollToAnchor } from "../../utils/scrollToAnchor";
import "./VehicleListToolbar.scss";

const matchesModelSearch = (item, tipoKey, query) => {
  const term = query.trim().toLowerCase();
  if (!term) return true;

  const info = item[tipoKey];
  if (!info) return false;

  const modelo = (info.modelo || "").toLowerCase();
  const version = (info.version || "").toLowerCase();
  return modelo.includes(term) || version.includes(term);
};

const VehicleList = ({ vehicles }) => {
  const [tipoSearch, setTipoSearch] = useState("todos");
  const [modelSearch, setModelSearch] = useState("");

  const history = useHistory();
  const location = useLocation();
  const { tipo } = useParams();

  const { financiaciones, loading: loadingFinanciaciones } = useFinanciaciones();

  useEffect(() => {
    const validTipos = ["todos", "convencional", "plan", "preventa", "adjudicado", "adjudicados"];
    const search = location.search;

    if (!tipo || !validTipos.includes(tipo)) {
      saveUtmParams();
      history.replace(buildRouteWithUtms("/todos", search));
    }
  }, [history, tipo, location.search]);

  useEffect(() => {
    setTipoSearch(tipo === undefined ? "todos" : tipo);
  }, [tipo]);

  const handleTipo = (nextTipo) => {
    setTipoSearch(nextTipo);
    saveUtmParams();

    const routeMap = {
      todos: "/todos",
      convencional: "/convencional",
      plan: "/plan",
      adjudicados: "/adjudicados",
    };
    const route = routeMap[nextTipo] || "/todos";
    const currentTipo = tipo || "todos";

    if (currentTipo !== nextTipo) {
      history.push(buildRouteWithUtms(route, location.search));
    }

    const sectionMap = {
      todos: "scroll-click",
      convencional: "convencional",
      plan: "plan",
      adjudicados: "adjudicados",
    };
    window.setTimeout(() => {
      scrollToAnchor(sectionMap[nextTipo] || "scroll-click");
    }, currentTipo !== nextTipo ? 80 : 0);
  };

  const filtradosConvencional = useMemo(
    () =>
      vehicles
        .filter((item) => item?.convencional?.habilitado === true)
        .filter((item) => matchesModelSearch(item, "convencional", modelSearch))
        .sort((a, b) => Number(a.convencional.prioridad) - Number(b.convencional.prioridad)),
    [vehicles, modelSearch],
  );

  const filtradosPlan = useMemo(
    () =>
      vehicles
        .filter((item) => item?.plan?.habilitado === true)
        .filter((item) => matchesModelSearch(item, "plan", modelSearch))
        .sort((a, b) => Number(a.plan.prioridad) - Number(b.plan.prioridad)),
    [vehicles, modelSearch],
  );

  const hasAdjudicados = vehicles.some((item) => item?.adjudicado?.habilitado === true);

  const filtradosAdjudicados = useMemo(
    () =>
      vehicles
        .filter((item) => item?.adjudicado?.habilitado === true)
        .filter((item) => matchesModelSearch(item, "adjudicado", modelSearch))
        .sort((a, b) => Number(a.adjudicado?.prioridad || 0) - Number(b.adjudicado?.prioridad || 0)),
    [vehicles, modelSearch],
  );

  const isSearching = Boolean(modelSearch.trim());
  const showConvencional =
    (tipoSearch === "convencional" || tipoSearch === "todos" || isSearching) &&
    (!isSearching || filtradosConvencional.length > 0);
  const showPlan =
    (tipoSearch === "plan" || tipoSearch === "todos" || isSearching) &&
    (!isSearching || filtradosPlan.length > 0);
  const showAdjudicadosSection =
    ((tipoSearch === "adjudicados" || tipoSearch === "todos") && filtradosAdjudicados.length > 0 && !isSearching) ||
    (isSearching && filtradosAdjudicados.length > 0);

  const noSearchResults =
    isSearching &&
    filtradosConvencional.length === 0 &&
    filtradosPlan.length === 0 &&
    filtradosAdjudicados.length === 0;

  return (
    <>
      <div id="scroll-click" className="vehicle-list-toolbar pt-4 pb-4">
        <div className="vehicle-list-toolbar-inner">
          <div className="vehicle-list-toolbar-buttons">
            <button
              type="button"
              className={`btn btn-blue_white ${tipoSearch === "todos" ? "active" : ""}`}
              onClick={() => handleTipo("todos")}
            >
              Ver todo
            </button>
            <button
              type="button"
              className={`btn btn-blue_white ${tipoSearch === "convencional" ? "active" : ""}`}
              onClick={() => handleTipo("convencional")}
            >
              Venta directa
            </button>
            <button
              type="button"
              className={`btn btn-blue_white ${tipoSearch === "plan" ? "active" : ""}`}
              onClick={() => handleTipo("plan")}
            >
              Plan de Ahorro
            </button>
            {hasAdjudicados ? (
              <button
                type="button"
                className={`btn btn-blue_white ${tipoSearch === "adjudicados" ? "active" : ""}`}
                onClick={() => handleTipo("adjudicados")}
              >
                Adjudicados
              </button>
            ) : null}
          </div>
          <div className="vehicle-list-search">
            <FiSearch className="vehicle-list-search__icon" aria-hidden="true" />
            <input
              type="search"
              className="vehicle-list-search__input"
              placeholder="Buscar modelo..."
              value={modelSearch}
              onChange={(e) => setModelSearch(e.target.value)}
              aria-label="Buscar modelo"
            />
            {modelSearch ? (
              <button
                type="button"
                className="vehicle-list-search__clear"
                onClick={() => setModelSearch("")}
                aria-label="Borrar búsqueda"
              >
                <FiX aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {noSearchResults ? (
        <p className="text-center py-4 vehicle-list-empty-search w-100">No encontramos modelos con ese nombre.</p>
      ) : null}

      {showConvencional ? (
        <div id="convencional">
          <div className="vehicle-list-section-header">
            <Container>
              <Row>
                <Col>
                  <h2 className="vehicle-list-section-title text-center">
                    ¡Llevate tu vehículo en Montironi por <b>Venta Directa</b>!
                  </h2>
                </Col>
              </Row>
            </Container>
          </div>

          <Row className="d-flex">
            {filtradosConvencional.length > 0 ? (
              filtradosConvencional.map((item, index) => (
                <Col lg={4} md={6} xs={12} key={index} className="vehicle-card-col d-flex mb-4">
                  <VehicleCard
                    info={item}
                    tipo={tipoSearch}
                    filtrados={filtradosConvencional}
                    nombreTipo="convencional"
                    todosLosVehiculos={vehicles}
                  />
                </Col>
              ))
            ) : (
              <p className="text-center py-4 vehicle-list-empty-search w-100">
                {modelSearch.trim()
                  ? "No encontramos modelos con ese nombre."
                  : "Proximamente más opciones disponibles."}
              </p>
            )}
          </Row>
        </div>
      ) : null}

      {showConvencional && loadingFinanciaciones ? (
        <Row className="d-flex justify-content-center mb-4 mt-2">
          <img src={loading} alt="Cargando..." style={{ width: 100, height: 100 }} />
        </Row>
      ) : null}

      {showConvencional && !loadingFinanciaciones && financiaciones && financiaciones.length > 0 ? (
        <div id="financiaciones" className="financing-section">
          <p className="financing-section-subtitle">Financiá por Venta Directa</p>
          <FinancingCarousel financiaciones={financiaciones} />
        </div>
      ) : null}

      {showPlan ? (
        <div className={tipoSearch === "plan" && !isSearching ? null : "mt-4"} id="plan">
          <div className="vehicle-list-section-header">
            <Container>
              <Row>
                <Col>
                  <h2 className="vehicle-list-section-title text-center">
                    ¡Tu 0km con <b>Plan de ahorro</b> en Montironi!
                  </h2>
                </Col>
              </Row>
            </Container>
          </div>
          <Row className="d-flex">
            {filtradosPlan.length > 0 ? (
              filtradosPlan.map((item, index) => (
                <Col lg={4} md={6} xs={12} key={index} className="vehicle-card-col d-flex mb-4">
                  <VehicleCard
                    info={item}
                    tipo={tipoSearch}
                    filtrados={filtradosPlan}
                    nombreTipo="plan"
                    todosLosVehiculos={vehicles}
                  />
                </Col>
              ))
            ) : (
              <p className="text-center py-4 vehicle-list-empty-search w-100">
                {modelSearch.trim()
                  ? "No encontramos modelos con ese nombre."
                  : "Proximamente más opciones disponibles."}
              </p>
            )}
          </Row>
        </div>
      ) : null}

      {showAdjudicadosSection ? (
        <div className="mt-4" id="adjudicados">
          <div className="vehicle-list-section-header">
            <Container>
              <Row>
                <Col>
                  <h2 className="vehicle-list-section-title text-center">Planes adjudicados</h2>
                </Col>
              </Row>
            </Container>
          </div>
          <Row className="d-flex">
            {filtradosAdjudicados.map((item, index) => (
              <Col lg={4} md={6} xs={12} key={index} className="vehicle-card-col d-flex mb-4">
                <VehicleCard
                  info={item}
                  tipo={tipoSearch}
                  filtrados={filtradosAdjudicados}
                  nombreTipo="adjudicado"
                  todosLosVehiculos={vehicles}
                />
              </Col>
            ))}
          </Row>
        </div>
      ) : null}
    </>
  );
};

export default VehicleList;
